import React, { useState, useMemo } from 'react';
import { Info, Trash2 } from 'lucide-react';
import { Dataset, DataRelationship, WorkspaceProps } from '../types';
import RelationshipGraph from './RelationshipGraph';

const Panel: React.FC<{ children: React.ReactNode, className?: string }> = ({ children, className }) => (
    <div className={`glass-panel border border-white/10 rounded-2xl shadow-xl ${className}`}>
        {children}
    </div>
);

const Header: React.FC<{ title: string, children?: React.ReactNode }> = ({ title, children }) => (
    <header className="flex justify-between items-center mb-6">
        <h2 className="text-2xl lg:text-3xl font-bold text-gray-200 tracking-tight">{title}</h2>
        {children}
    </header>
);

const SelectField: React.FC<{ name: string, label?: string, value: string | undefined, onChange?: (e: React.ChangeEvent<HTMLSelectElement>) => void, options?: { label: string, value: string }[], disabled?: boolean, children?: React.ReactNode }> = ({ name, label, value, onChange, options, disabled, children }) => (
    <div className="space-y-1.5">
        {label && <label className="text-sm font-semibold text-gray-400">{label}</label>}
        <select name={name} value={value || ''} onChange={onChange} className="w-full text-base min-w-0 px-3 py-2 border border-white/20 rounded-lg bg-white/5 text-white focus:ring-2 focus:ring-neon-gold focus:border-neon-gold transition-colors disabled:bg-white/5 disabled:cursor-not-allowed" disabled={disabled}>
            {children ? children : <>
                <option value="" className="bg-obsidian-900 text-gray-400">Select Field</option>
                {options?.map(c => <option key={c.value} value={c.value} className="bg-obsidian-900 text-white">{c.label}</option>)}
            </>}
        </select>
    </div>
);

const DataRelationshipsPage: React.FC<Pick<WorkspaceProps, 'datasets' | 'relationships' | 'setRelationships'>> = ({ datasets, relationships, setRelationships }) => {
    const [formState, setFormState] = useState({ dsA: '', dsB: '', fieldA: '', fieldB: '' });
    const datasetA = useMemo(() => datasets.find(d => d.id === formState.dsA), [datasets, formState.dsA]);
    const datasetB = useMemo(() => datasets.find(d => d.id === formState.dsB), [datasets, formState.dsB]);

    const handleSave = () => {
        if (!formState.dsA || !formState.dsB || !formState.fieldA || !formState.fieldB) {
            alert("Please fill out all fields.");
            return;
        }
        if (formState.dsA === formState.dsB) {
            alert("Please select two different datasets.");
            return;
        }

        const newRelationship: DataRelationship = {
            id: `rel-${Date.now()}`,
            datasetAId: formState.dsA,
            datasetBId: formState.dsB,
            fieldA: formState.fieldA,
            fieldB: formState.fieldB,
        };
        setRelationships(prev => [...prev, newRelationship]);
        setFormState({ dsA: '', dsB: '', fieldA: '', fieldB: '' });
    };

    const handleDelete = (id: string) => {
        setRelationships(prev => prev.filter(r => r.id !== id));
    };

    // Find suggested relationships
    const suggestedRelationships = useMemo(() => {
        const suggestions: Array<{ dsA: Dataset; dsB: Dataset; fieldA: string; fieldB: string; }> = [];
        for (let i = 0; i < datasets.length; i++) {
            for (let j = i + 1; j < datasets.length; j++) {
                const dsA = datasets[i];
                const dsB = datasets[j];
                // Check if relationship already exists
                const hasExplicit = relationships.some(r =>
                    (r.datasetAId === dsA.id && r.datasetBId === dsB.id) ||
                    (r.datasetBId === dsA.id && r.datasetAId === dsB.id)
                );
                if (hasExplicit) continue;

                // Find matching fields
                for (const ac of dsA.schema) {
                    for (const bc of dsB.schema) {
                        const sameName = ac.name.toLowerCase() === bc.name.toLowerCase();
                        const bothNumeric = ac.type === 'number' && bc.type === 'number';
                        const bothStringLike = (ac.type !== 'number') && (bc.type !== 'number');
                        if (sameName && (bothNumeric || bothStringLike)) {
                            suggestions.push({ dsA, dsB, fieldA: ac.name, fieldB: bc.name });
                            break;
                        }
                    }
                }
            }
        }
        return suggestions;
    }, [datasets, relationships]);

    const createSuggestedRelationship = (suggestion: typeof suggestedRelationships[0]) => {
        const newRelationship: DataRelationship = {
            id: `rel-${Date.now()}`,
            datasetAId: suggestion.dsA.id,
            datasetBId: suggestion.dsB.id,
            fieldA: suggestion.fieldA,
            fieldB: suggestion.fieldB,
        };
        setRelationships(prev => [...prev, newRelationship]);
    };

    return (
        <div className="space-y-8">
            <Header title="Data Connections" />
            <div className="p-4 bg-neon-gold/10 border border-neon-gold/20 rounded-lg text-neon-gold flex items-center gap-3">
                <Info className="w-5 h-5 text-neon-gold flex-shrink-0" />
                Connections are automatically detected when datasets share matching field names. You can also create custom relationships below.
            </div>

            {suggestedRelationships.length > 0 && (
                <Panel className="p-4">
                    <h3 className="text-lg font-bold text-gray-200 mb-4 flex items-center gap-2"> Suggested Relationships</h3>
                    <div className="space-y-2">
                        {suggestedRelationships.map((suggestion, idx) => (
                            <div key={idx} className="flex items-center justify-between p-3 bg-emerald-900/10 border border-emerald-500/20 rounded-lg hover:bg-emerald-900/20 transition-colors">
                                <div className="flex-1">
                                    <p className="text-sm font-medium text-gray-200">
                                        <span className="text-emerald-400 font-bold">{suggestion.dsA.name}</span>
                                        {' ↔ '}
                                        <span className="text-emerald-400 font-bold">{suggestion.dsB.name}</span>
                                    </p>
                                    <p className="text-xs text-white/80 mt-1">
                                        via <code className="bg-black/50 text-neon-gold px-1.5 py-0.5 rounded border border-white/10">{suggestion.fieldA}</code>
                                    </p>
                                </div>
                                <button
                                    onClick={() => createSuggestedRelationship(suggestion)}
                                    className="ml-3 px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-500 transition-colors shadow-lg shadow-emerald-900/20"
                                >
                                    Create
                                </button>
                            </div>
                        ))}
                    </div>
                </Panel>
            )}

            <Panel className="p-4">
                <h3 className="text-lg font-bold text-gray-200 mb-4">Connections Map</h3>
                <RelationshipGraph datasets={datasets} relationships={relationships} />
            </Panel>

            <Panel className="p-4">
                <h3 className="text-lg font-bold text-gray-200 mb-4">Create Custom Relationship</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-4">
                        <h4 className="font-semibold text-gray-300 text-sm">Dataset A</h4>
                        <SelectField label="Dataset" name="dsA" value={formState.dsA} onChange={e => setFormState(s => ({ ...s, dsA: e.target.value, fieldA: '' }))}>
                            <option value="" className="bg-obsidian-900 text-gray-400">Select Dataset</option>
                            {datasets.map(ds => <option key={ds.id} value={ds.id} className="bg-obsidian-900 text-white">{ds.name}</option>)}
                        </SelectField>
                        <SelectField label="Field" name="fieldA" value={formState.fieldA} onChange={e => setFormState(s => ({ ...s, fieldA: e.target.value }))} disabled={!datasetA}>
                            <option value="" className="bg-obsidian-900 text-gray-400">Select Field</option>
                            {datasetA?.schema.map(col => <option key={col.name} value={col.name} className="bg-obsidian-900 text-white">{col.name} ({col.type})</option>)}
                        </SelectField>
                    </div>
                    <div className="space-y-4">
                        <h4 className="font-semibold text-gray-300 text-sm">Dataset B</h4>
                        <SelectField label="Dataset" name="dsB" value={formState.dsB} onChange={e => setFormState(s => ({ ...s, dsB: e.target.value, fieldB: '' }))}>
                            <option value="" className="bg-obsidian-900 text-gray-400">Select Dataset</option>
                            {datasets.map(ds => <option key={ds.id} value={ds.id} className="bg-obsidian-900 text-white">{ds.name}</option>)}
                        </SelectField>
                        <SelectField label="Field" name="fieldB" value={formState.fieldB} onChange={e => setFormState(s => ({ ...s, fieldB: e.target.value }))} disabled={!datasetB}>
                            <option value="" className="bg-obsidian-900 text-gray-400">Select Field</option>
                            {datasetB?.schema.map(col => <option key={col.name} value={col.name} className="bg-obsidian-900 text-white">{col.name} ({col.type})</option>)}
                        </SelectField>
                    </div>
                </div>
                <button onClick={handleSave} className="mt-6 bg-neon-gold text-white font-semibold px-5 py-2.5 rounded-xl hover:bg-neon-gold/80 transition-colors border border-neon-gold/50 shadow-[0_0_15px_rgba(245,158,11,0.3)]">
                    Create Relationship
                </button>
            </Panel>

            <div className="hidden">
                {relationships.length === 0 ? (
                    <p className="text-gray-500">No relationships defined yet.</p>
                ) : (
                    <Panel className="divide-y divide-gray-200">
                        {relationships.map(rel => {
                            const dsA = datasets.find(d => d.id === rel.datasetAId);
                            const dsB = datasets.find(d => d.id === rel.datasetBId);
                            if (!dsA || !dsB) return null;
                            return (
                                <div key={rel.id} className="p-4 flex justify-between items-center">
                                    <div>
                                        <p className="font-medium text-gray-800">
                                            <span className="text-gold-600">{dsA.name}</span> joined to <span className="text-gold-600">{dsB.name}</span>
                                        </p>
                                        <p className="text-sm text-gray-500">
                                            on <span className="font-mono bg-gray-100 p-1 rounded-md text-xs">{dsA.name}.{rel.fieldA}</span> = <span className="font-mono bg-gray-100 p-1 rounded-md text-xs">{dsB.name}.{rel.fieldB}</span>
                                        </p>
                                    </div>
                                    <button onClick={() => handleDelete(rel.id)} className="p-2 rounded-md hover:bg-red-50 text-red-500 hover:text-red-600">
                                        <Trash2 className="h-4 w-4" />
                                    </button>
                                </div>
                            );
                        })}
                    </Panel>
                )}
            </div>
        </div>
    );
};

export default DataRelationshipsPage;
